import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, Loader2, User, MapPin, Phone, Mail, Calendar, BookOpen, Award, Download, Eye, CheckCircle, XCircle, Clock, RefreshCw } from "lucide-react";

const JOBS_API = "https://nspo-backend-gray.vercel.app/api/jobs";
const APPLICANTS_API = "https://nspo-backend-gray.vercel.app/api/applicants/by-job";
const UPDATE_STATUS_API = "https://nspo-backend-gray.vercel.app/api/applications";

const ApplicantsTab = () => {
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState("");
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [viewMode, setViewMode] = useState("table");

  const token = localStorage.getItem("adminToken");

  // 🟢 Fetch jobs for dropdown
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await fetch(JOBS_API, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json();
        setJobs(data);
      } catch {
        toast.error("❌ فشل في تحميل الوظائف");
      }
    };
    fetchJobs();
  }, [token]);

  // 🟡 Fetch applicants by job
  const fetchApplicants = async (jobId) => {
    if (!jobId) return;
    setLoading(true);
    setApplicants([]);
    try {
      const res = await fetch(`${APPLICANTS_API}/${jobId}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      setApplicants(data);
      if (data.length === 0) {
        toast.info("لا يوجد متقدمين لهذه الوظيفة 👀");
      } else {
        toast.success(`تم تحميل ${data.length} متقدم`);
      }
    } catch {
      toast.error("❌ فشل في تحميل المتقدمين");
    } finally {
      setLoading(false);
    }
  };

  // 🟠 Download CV
  const handleDownloadCV = async (applicant) => {
    try {
      const link = document.createElement("a");
      link.href = applicant.cvUrl;
      link.download = `CV_${applicant.fullName}_${applicant.nationalId}.${applicant.cvPath.split('.').pop()}`;
      link.click();
      toast.success("📄 تم تحميل السيرة الذاتية", {
        description: `تم تحميل CV ${applicant.fullName}`,
      });
    } catch {
      toast.error("❌ فشل في تحميل الملف");
    }
  };

  // 🟣 View CV in new tab
  const handleViewCV = (applicant) => {
    window.open(applicant.cvUrl, '_blank');
  };

  // 🔵 Update applicant status
  const handleUpdateStatus = async (applicantId, jobId, newStatus) => {
    setUpdatingStatus(applicantId);
    try {
      const res = await fetch(`${UPDATE_STATUS_API}/${applicantId}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          jobId: jobId,
          status: newStatus
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "فشل في تحديث الحالة");
      }

      // Update local state
      setApplicants(prev => prev.map(applicant => {
        if (applicant._id === applicantId) {
          return { ...applicant, status: newStatus };
        }
        return applicant;
      }));

      toast.success("✅ تم تحديث الحالة بنجاح", {
        description: `تم تغيير الحالة إلى ${getStatusInfo(newStatus).text}`,
      });

    } catch (error) {
      toast.error("❌ فشل في تحديث الحالة", {
        description: error.message,
      });
    } finally {
      setUpdatingStatus(null);
    }
  };

  // 🟠 Get status color and text
  const getStatusInfo = (status) => {
    switch (status) {
      case "accepted":
        return { 
          color: "bg-green-100 text-green-800 border-green-200", 
          text: "مقبول",
          icon: CheckCircle,
          iconColor: "text-green-600"
        };
      case "rejected":
        return { 
          color: "bg-red-100 text-red-800 border-red-200", 
          text: "مرفوض",
          icon: XCircle,
          iconColor: "text-red-600"
        };
      case "under-review":
        return { 
          color: "bg-blue-100 text-blue-800 border-blue-200", 
          text: "قيد المراجعة",
          icon: RefreshCw,
          iconColor: "text-blue-600"
        };
      default:
        return { 
          color: "bg-yellow-100 text-yellow-800 border-yellow-200", 
          text: "قيد الانتظار",
          icon: Clock,
          iconColor: "text-yellow-600"
        };
    }
  };

  // Calculate age from birth date
  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Status options
  const statusOptions = [
    { value: "pending", label: "قيد الانتظار", color: "text-yellow-600" },
    { value: "under-review", label: "قيد المراجعة", color: "text-blue-600" },
    { value: "accepted", label: "مقبول", color: "text-green-600" },
    { value: "rejected", label: "مرفوض", color: "text-red-600" }
  ];

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8 gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <User className="w-8 h-8 text-blue-600" />
            إدارة المتقدمين
          </h2>
          <p className="text-gray-600 mt-2">عرض وإدارة المتقدمين للوظائف</p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setViewMode("table")}
              className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                viewMode === "table" 
                  ? "bg-white text-blue-600 shadow-sm" 
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              جدول
            </button>
            <button
              onClick={() => setViewMode("cards")}
              className={`px-4 py-2 rounded-lg transition-all duration-200 ${
                viewMode === "cards" 
                  ? "bg-white text-blue-600 shadow-sm" 
                  : "text-gray-600 hover:text-gray-800"
              }`}
            >
              بطاقات
            </button>
          </div>

          {/* Job Selector */}
          <select
            className="border border-gray-300 rounded-xl p-3 w-64 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            value={selectedJob}
            onChange={(e) => {
              setSelectedJob(e.target.value);
              fetchApplicants(e.target.value);
            }}
          >
            <option value="">-- اختر الوظيفة --</option>
            {jobs.map((job) => (
              <option key={job._id} value={job._id}>
                {job.title} - {job.company}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <Loader2 className="animate-spin w-8 h-8 text-blue-600 mx-auto mb-3" />
            <p className="text-gray-600">جاري تحميل بيانات المتقدمين...</p>
          </div>
        </div>
      )}

      {/* Applicants Count */}
      {!loading && selectedJob && applicants.length > 0 && (
        <div className="mb-6 p-4 bg-blue-50 rounded-2xl border border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-900">
                عدد المتقدمين: <span className="text-2xl">{applicants.length}</span>
              </h3>
              <p className="text-blue-700 text-sm">
                {jobs.find(j => j._id === selectedJob)?.title} - {jobs.find(j => j._id === selectedJob)?.company}
              </p>
            </div>
            <div className="flex gap-4 text-sm">
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full">
                مقبول: {applicants.filter(a => a.status === 'accepted').length}
              </span>
              <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full">
                في الانتظار: {applicants.filter(a => a.status === 'pending').length}
              </span>
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                تحت المراجعة: {applicants.filter(a => a.status === 'under-review').length}
              </span>
              <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full">
                مرفوض: {applicants.filter(a => a.status === 'rejected').length}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Table View */}
      {!loading && viewMode === "table" && applicants.length > 0 && (
        <div className="overflow-x-auto rounded-2xl border border-gray-200">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-4 text-right font-semibold text-gray-700">المعلومات الشخصية</th>
                <th className="p-4 text-right font-semibold text-gray-700">التعليم والخبرة</th>
                <th className="p-4 text-right font-semibold text-gray-700">معلومات الاتصال</th>
                <th className="p-4 text-center font-semibold text-gray-700">الحالة</th>
                <th className="p-4 text-center font-semibold text-gray-700">الإجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {applicants.map((applicant) => {
                const statusInfo = getStatusInfo(applicant.status);
                const StatusIcon = statusInfo.icon;
                const jobId = applicant.appliedJobs?.[0]?.job;
                
                return (
                  <tr key={applicant._id} className="hover:bg-gray-50 transition-colors duration-200">
                    <td className="p-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-500" />
                          <span className="font-semibold text-gray-900">{applicant.fullName}</span>
                        </div>
                        <div className="text-sm text-gray-600">
                          الرقم القومي: {applicant.nationalId}
                        </div>
                        <div className="text-sm text-gray-600">
                          العمر: {calculateAge(applicant.birthDate)} سنة
                        </div>
                      </div>
                    </td>
                    
                    <td className="p-4">
                      <div className="space-y-2">
                        {applicant.education?.degree && (
                          <div className="flex items-center gap-2">
                            <BookOpen className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">{applicant.education.degree}</span>
                          </div>
                        )}
                        {applicant.totalExperienceYears && (
                          <div className="flex items-center gap-2">
                            <Award className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">خبرة: {applicant.totalExperienceYears} سنة</span>
                          </div>
                        )}
                      </div>
                    </td>
                    
                    <td className="p-4">
                      <div className="space-y-2">
                        {applicant.contactInfo?.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">{applicant.contactInfo.phone}</span>
                          </div>
                        )}
                        {applicant.contactInfo?.governorate && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-500" />
                            <span className="text-sm">{applicant.contactInfo.governorate}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    
                    <td className="p-4">
                      <div className="flex flex-col items-center gap-3">
                        <div className="flex items-center gap-2">
                          <StatusIcon className={`w-4 h-4 ${statusInfo.iconColor}`} />
                          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusInfo.color}`}>
                            {statusInfo.text}
                          </span>
                        </div>
                        
                        <select
                          value={applicant.status}
                          onChange={(e) => handleUpdateStatus(applicant._id, jobId, e.target.value)}
                          disabled={updatingStatus === applicant._id}
                          className="text-xs border border-gray-300 rounded-lg p-2 focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                        >
                          {statusOptions.map(option => (
                            <option key={option.value} value={option.value} className={option.color}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        
                        {updatingStatus === applicant._id && (
                          <Loader2 className="w-3 h-3 animate-spin text-blue-600" />
                        )}
                      </div>
                    </td>
                    
                    <td className="p-4">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => handleViewCV(applicant)}
                          className="flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-xl hover:bg-blue-700 transition-colors duration-200 text-sm"
                        >
                          <Eye size={14} />
                          عرض
                        </button>
                        <button
                          onClick={() => handleDownloadCV(applicant)}
                          className="flex items-center gap-2 bg-green-600 text-white px-3 py-2 rounded-xl hover:bg-green-700 transition-colors duration-200 text-sm"
                        >
                          <Download size={14} />
                          تحميل
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Cards View */}
      {!loading && viewMode === "cards" && applicants.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {applicants.map((applicant) => {
            const statusInfo = getStatusInfo(applicant.status);
            const StatusIcon = statusInfo.icon;
            const jobId = applicant.appliedJobs?.[0]?.job;
            
            return (
              <div
                key={applicant._id}
                className="border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300 bg-white"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <User className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg">{applicant.fullName}</h3>
                      <p className="text-gray-600 text-sm">الرقم القومي: {applicant.nationalId}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusIcon className={`w-4 h-4 ${statusInfo.iconColor}`} />
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${statusInfo.color}`}>
                      {statusInfo.text}
                    </span>
                  </div>
                </div>

                {/* Personal Info */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>العمر: {calculateAge(applicant.birthDate)} سنة</span>
                  </div>
                  
                  {applicant.education?.degree && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <BookOpen className="w-4 h-4" />
                      <span>{applicant.education.degree}</span>
                    </div>
                  )}
                  
                  {applicant.contactInfo?.phone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone className="w-4 h-4" />
                      <span>{applicant.contactInfo.phone}</span>
                    </div>
                  )}
                </div>

                {/* Status Update */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">تغيير الحالة:</label>
                  <div className="flex items-center gap-2">
                    <select
                      value={applicant.status}
                      onChange={(e) => handleUpdateStatus(applicant._id, jobId, e.target.value)}
                      disabled={updatingStatus === applicant._id}
                      className="flex-1 border border-gray-300 rounded-lg p-2 text-sm focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                    >
                      {statusOptions.map(option => (
                        <option key={option.value} value={option.value} className={option.color}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {updatingStatus === applicant._id && (
                      <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleViewCV(applicant)}
                    className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white py-2 rounded-xl hover:bg-blue-700 transition-colors duration-200 text-sm"
                  >
                    <Eye size={14} />
                    عرض السيرة الذاتية
                  </button>
                  <button
                    onClick={() => handleDownloadCV(applicant)}
                    className="flex-1 flex items-center justify-center gap-2 bg-green-600 text-white py-2 rounded-xl hover:bg-green-700 transition-colors duration-200 text-sm"
                  >
                    <Download size={14} />
                    تحميل
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!loading && selectedJob && applicants.length === 0 && (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-10 h-10 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-600 mb-2">لا يوجد متقدمين</h3>
          <p className="text-gray-500">لم يتقدم أي شخص على هذه الوظيفة بعد</p>
        </div>
      )}

      {/* No Job Selected */}
      {!loading && !selectedJob && (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-10 h-10 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-600 mb-2">اختر وظيفة</h3>
          <p className="text-gray-500">يرجى اختيار وظيفة لعرض المتقدمين عليها</p>
        </div>
      )}
    </div>
  );
};

export default ApplicantsTab;
